/**
 * Vorgangsfolge als Organigramm.
 *
 * Zeigt alle Belege eines Vorgangs als Baum: Angebot ganz oben, darunter der
 * Auftrag, daraus verzweigt die Lieferscheine und Rechnungen, ein Storno haengt
 * an dem Beleg, den er aufhebt. Eigene, breite Seite - die Belegakte selbst
 * bleibt schlank. Von jedem Kasten aus ein Klick zum Beleg.
 */

import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { formatAmount, formatDate, getDocumentType, type DocumentType } from '@erp/shared';

import { api } from '../api/client';
import { PageHeader } from '../components/Layout';
import type { ChainEntry } from '../components/DocumentChain';
import { DOCUMENT_ROUTES } from './DocumentsPage';
import { LoadingBlock, EmptyState } from '../components/ui';

type ChainResponse = { chain: ChainEntry[]; partnerName: string | null };

type TreeNode = ChainEntry & { children: TreeNode[]; isCancellation: boolean };

/** Baut aus der flachen Kette den Baum: Vorgaenger ist der Elternknoten, ein
 *  Storno haengt an dem Beleg, den er aufhebt. */
function buildTree(chain: ChainEntry[]): TreeNode[] {
  const byId = new Map<string, TreeNode>();
  for (const entry of chain) {
    byId.set(entry.id, { ...entry, children: [], isCancellation: Boolean(entry.cancelsDocumentId) });
  }
  const roots: TreeNode[] = [];
  for (const node of byId.values()) {
    const parentId = node.cancelsDocumentId ?? node.sourceDocumentId;
    const parent = parentId ? byId.get(parentId) : null;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  const sortNodes = (nodes: TreeNode[]) => {
    nodes.sort((a, b) =>
      a.documentDate !== b.documentDate
        ? a.documentDate < b.documentDate
          ? -1
          : 1
        : (a.number ?? '').localeCompare(b.number ?? ''),
    );
    for (const n of nodes) sortNodes(n.children);
  };
  sortNodes(roots);
  return roots;
}

function NodeCard({ node }: { node: TreeNode }) {
  const meta = getDocumentType(node.type as DocumentType);
  const cancelled = node.lifecycle === 'cancelled';
  const label = node.isCancellation ? `Storno ${meta.label}` : meta.label;
  const className = [
    'vorgang-node',
    node.current ? 'vorgang-node-current' : '',
    node.isCancellation || cancelled ? 'vorgang-node-storno' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const inner = (
    <>
      <div className="vorgang-node-type">{label}</div>
      <div className="vorgang-node-number mono">{node.number ?? 'Entwurf'}</div>
      <div className="vorgang-node-meta">
        {formatDate(node.documentDate)} · {formatAmount(node.grossTotal)} €
      </div>
    </>
  );

  return node.current ? (
    <div className={className} aria-current="page">
      {inner}
    </div>
  ) : (
    <Link className={className} to={`${DOCUMENT_ROUTES[node.type]}/${node.id}`}>
      {inner}
    </Link>
  );
}

/** Ein Knoten mit seinen Kindern - klassischer CSS-Baum ueber verschachtelte Listen. */
function TreeBranch({ node }: { node: TreeNode }) {
  return (
    <li>
      <NodeCard node={node} />
      {node.children.length > 0 ? (
        <ul>
          {node.children.map((child) => (
            <TreeBranch key={child.id} node={child} />
          ))}
        </ul>
      ) : null}
    </li>
  );
}

export function VorgangPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError, refetch, isRefetching } = useQuery({
    queryKey: ['vorgang', id],
    queryFn: () => api.get<ChainResponse>(`/api/documents/${id}/chain`),
    enabled: Boolean(id),
  });

  const roots = useMemo(() => (data ? buildTree(data.chain) : []), [data]);

  return (
    <>
      <PageHeader
        title={data?.partnerName ? `Vorgangsfolge · ${data.partnerName}` : 'Vorgangsfolge'}
        subtitle={data?.partnerName ? `Alle Belege dieses Vorgangs · ${data.partnerName}` : 'Alle Belege dieses Vorgangs'}
        actions={
          <button type="button" className="btn" onClick={() => window.history.back()}>
            Zurück
          </button>
        }
      />
      <div className="card">
        <div className="card-body">
          {isLoading ? <LoadingBlock /> : null}
          {isError ? (
            <div style={{ textAlign: 'center' }}>
              <EmptyState
                title="Vorgang konnte nicht geladen werden"
                hint="Der Beleg ist nicht erreichbar. Bitte erneut versuchen."
              />
              <button
                type="button"
                className="btn"
                onClick={() => refetch()}
                disabled={isRefetching}
                style={{ marginTop: 12 }}
              >
                {isRefetching ? 'Wird geladen …' : 'Erneut versuchen'}
              </button>
            </div>
          ) : null}
          {data && data.chain.length === 0 ? (
            <EmptyState title="Kein Vorgang" hint="Zu diesem Beleg gibt es keine verknüpften Belege." />
          ) : null}
          {data && roots.length > 0 ? (
            <div className="vorgang-tree-wrap">
              <ul className="vorgang-tree">
                {roots.map((root) => (
                  <TreeBranch key={root.id} node={root} />
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
